import React, { useEffect, useState, useMemo } from "react";
import Skeleton from "@mui/material/Skeleton";
import Report from "@/components/Report";
import useReportsStore from "@/hooks/useReports";
import useRecordingLinksStore from "@/hooks/useRecordingLinks";
import { useWorkspaceStore } from "@/hooks/useWorkspace";
import { useParams } from "react-router-dom";
import { API_URL } from "@/env";
import { useNavigate } from "react-router-dom";
import { getCookie } from "@/lib/cookies";
import SearchLg from "@/assets/icons/SearchLg.svg";
import useUserData from "@/hooks/useUserData";
import JoinWorkspaceFooter from "@/components/Reports/JoinWorkspaceFooter";
import { AnimatePresence } from "motion/react";
import { showCustomToast } from "@/components/CustomToast";
import { setCookie } from "@/lib/cookies";
import NoReports from "@/components/Reports/NoReports";
import NoResults from "@/components/Reports/NoResults";
import { profilePictures } from "@/lib/data";
import ChevronLeftWhite from "@/assets/icons/ChevronLeftWhite.svg";
import RecordingLinkOptions from "@/components/Reports/RecordingLinkOptions";
import VerticalMenu from "@/assets/icons/VerticalMenu.svg";
import ConfirmDeleteRecordingLink from "@/components/Reports/ConfirmDeleteRecordingLink";
import EmptyState from "@/components/Reports/EmptyState";
import Link from "@/assets/icons/LinkBlack.svg";

const tabs = ["Library", "Shared Recordings"];

const Reports = () => {
  const params = useParams();
  const token = getCookie("token");
  const navigate = useNavigate();

  // Reports store
  const { reports, isLoading, initializeReports, setIsLoading } =
    useReportsStore();

  // Recording links store
  const {
    recordingLinks,
    isLoading: isLoadingLinks,
    setRecordingLinks,
    removeReportFromLink,
    setIsLoading: setIsLoadingLinks,
    removeRecordingLink,
  } = useRecordingLinksStore();

  const { currentWorkspace, setCurrentWorkspace, updateWorkspace } =
    useWorkspaceStore();
  const { user } = useUserData();

  const skeletonArr = [1, 2, 3, 4];
  const [reportsSearchTerm, setReportsSearchTerm] = useState("");
  const [recordingsSearchTerm, setRecordingsSearchTerm] = useState("");
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    return currentWorkspace?.activeReportsTab || "Library";
  });
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [recordingLinkMenus, setRecordingLinkMenus] = useState<{
    [key: string]: boolean;
  }>({});
  const [recordingLinkToDelete, setRecordingLinkToDelete] = useState<{
    token: string;
    name: string;
    count: number;
  } | null>(null);

  // Create refs for all recording link menus
  const menuRefs = React.useRef<{
    [key: string]: React.RefObject<HTMLDivElement>;
  }>({});

  // Get the current search term based on active tab
  const currentSearchTerm =
    activeTab === "Library" ? reportsSearchTerm : recordingsSearchTerm;

  // Set search term based on active tab
  const setCurrentSearchTerm = (value: string) => {
    if (activeTab === "Library") {
      setReportsSearchTerm(value);
    } else {
      setRecordingsSearchTerm(value);
    }
  };

  // Update local state when workspace changes
  // useEffect(() => {
  //   const activateToolbar = async () => {
  //     try {
  //       const response = await chrome.runtime.sendMessage(
  //         "aaiohempijgmakbmhiockdeoifcpoioc",
  //         { action: "activateFromWebsite" }
  //       );

  //       if (response.success) {
  //         console.log("Toolbar activated:", response.action);
  //       }
  //     } catch (error) {
  //       console.error("Error activating Toolbar:", error);
  //     }
  //   };

  //   activateToolbar();
  // }, []);

  // useEffect(() => {
  //   const activateToolbar = async () => {
  //     try {
  //       const response = await chrome.runtime.sendMessage(
  //         "aaiohempijgmakbmhiockdeoifcpoioc",
  //         { action: "activateFromWebsite" }
  //       );

  //       console.log("Response received:", response);

  //       if (response?.success) {
  //         console.log("✅ Toolbar activated:", response.action);
  //       } else {
  //         console.error("❌ Activation failed:", response);
  //       }
  //     } catch (error) {
  //       console.error("❌ Error activating Toolbar:", error);
  //     }
  //   };

  //   // Add a small delay to ensure DOM is ready
  //   const timer = setTimeout(() => {
  //     activateToolbar();
  //   }, 100);

  //   return () => clearTimeout(timer);
  // }, []);

  useEffect(() => {
    if (currentWorkspace?.activeReportsTab) {
      setActiveTab(currentWorkspace.activeReportsTab);
    } else {
      setActiveTab("Library");
    }
  }, [currentWorkspace?._id]);

  // Save active tab to workspace store
  useEffect(() => {
    if (
      currentWorkspace?._id &&
      currentWorkspace.activeReportsTab !== activeTab
    ) {
      updateWorkspace(currentWorkspace._id, { activeReportsTab: activeTab });
    }
  }, [activeTab, currentWorkspace?._id]);

  useEffect(() => {
    const fetchWorkspace = async () => {
      try {
        if (isInitialLoad) setIsLoading(true);
        const response = await fetch(
          `${API_URL}/workspaces/${params.workspaceId}`,
        );
        const data = await response.json();
        if (data._id) {
          setCurrentWorkspace(data);
        } else {
          if (!token) {
            navigate("/auth");
            setCurrentWorkspace(null);
          }
        }
      } catch (err) {
        console.log(err);
      } finally {
        setIsLoading(false);
        setIsInitialLoad(false);
      }
    };

    fetchWorkspace();
  }, [params.workspaceId]);

  // Fetch recording links when on Shared Recordings tab
  useEffect(() => {
    const fetchRecordingLinks = async () => {
      if (activeTab === "Shared Recordings" && currentWorkspace?._id && token) {
        setIsLoadingLinks(true);
        try {
          const response = await fetch(
            `${API_URL}/recording-links/workspace/${currentWorkspace._id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );
          const data = await response.json();

          if (data.success) {
            setRecordingLinks(data.links);
          }
        } catch (err) {
          console.error("Failed to fetch recording links:", err);
        } finally {
          setIsLoadingLinks(false);
        }
      }
    };

    fetchRecordingLinks();
  }, [activeTab, currentWorkspace?._id, token]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paramsToken = urlParams.get("token");
    const error = urlParams.get("error");

    if (paramsToken) {
      setCookie("token", paramsToken);
      // Remove all auth params
      urlParams.delete("token");
      urlParams.delete("username");
      urlParams.delete("profilePicture");
      showCustomToast("Logged in successfully", "check");
      const newSearch = urlParams.toString();
      const newUrl = newSearch
        ? `${window.location.pathname}?${newSearch}`
        : window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    } else if (error) {
      showCustomToast(`Authentication failed`, "exclamation");
      urlParams.delete("error");
      const newSearch = urlParams.toString();
      const newUrl = newSearch
        ? `${window.location.pathname}?${newSearch}`
        : window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  useEffect(() => {
    initializeReports(currentWorkspace?.reports);
  }, [currentWorkspace?._id]);

  // Activate Chrome extension on page load
  useEffect(() => {
    const activateExtension = async () => {
      const extensionId = "aaiohempijgmakbmhiockdeoifcpoioc";

      if (typeof chrome !== "undefined" && chrome.runtime) {
        try {
          await chrome.runtime.sendMessage(extensionId, { action: "activate" });
        } catch (error) {
          // console.log("Extension not installed or unavailable:", error);
        }
      }
    };

    activateExtension();
  }, []);

  // Reset selected folder when changing tabs
  useEffect(() => {
    setSelectedFolder(null);
  }, [activeTab]);

  // Filter regular reports (not from recording links)
  const filteredReports = useMemo(() => {
    let filtered = reports?.filter(
      (report: any) => !report?.isFromRecordingLink,
    );

    // Filter by search term
    if (reportsSearchTerm.trim()) {
      filtered = filtered?.filter((report: any) =>
        report.title
          ?.toLowerCase()
          .trim()
          .includes(reportsSearchTerm.toLowerCase().trim()),
      );
    }

    return filtered;
  }, [reports, reportsSearchTerm]);

  const handleReportDelete = (deletedReportId: string) => {
    removeReportFromLink(deletedReportId);
  };

  // Filter recording links by search term
  const filteredRecordingLinks = useMemo(() => {
    if (!recordingsSearchTerm.trim()) return recordingLinks;

    return recordingLinks.filter((link) => {
      const linkName = link.name || link.title || "Recording Link";
      return linkName
        .toLowerCase()
        .includes(recordingsSearchTerm.toLowerCase().trim());
    });
  }, [recordingLinks, recordingsSearchTerm]);

  // Setup click outside handlers for all recording link menus
  React.useEffect(() => {
    filteredRecordingLinks.forEach((link) => {
      const linkId = link._id;
      if (!menuRefs.current[linkId]) {
        menuRefs.current[linkId] = React.createRef();
      }
    });
  }, [filteredRecordingLinks]);

  // Handle click outside for each menu
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      Object.entries(menuRefs.current).forEach(([linkId, ref]) => {
        if (ref.current && !ref.current.contains(event.target as Node)) {
          setRecordingLinkMenus((prev) => ({ ...prev, [linkId]: false }));
        }
      });
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const renderContent = () => {
    const loading = activeTab === "Library" ? isLoading : isLoadingLinks;

    if (loading) {
      return skeletonArr.map((skeleton, i) => (
        <Skeleton
          key={i}
          sx={{ bgcolor: "rgba(255, 255, 255, 0.05)" }}
          className="rounded-[40px]"
          variant="rectangular"
          width="full"
          height={240}
        />
      ));
    }

    // Regular reports view
    if (activeTab === "Library") {
      if (filteredReports?.length === 0) {
        return reportsSearchTerm ? (
          <NoResults desc="We haven't found such a report, but who knows..." />
        ) : (
          <NoReports />
        );
      }

      return filteredReports.map((report: any) => (
        <Report key={report._id} report={report} />
      ));
    }

    // Shared recordings view
    if (activeTab === "Shared Recordings") {
      // If a folder is selected, show its reports
      if (selectedFolder) {
        const selectedLink = recordingLinks.find(
          (link) => link._id === selectedFolder,
        );

        if (!selectedLink) return null;

        const reports = selectedLink.reports || [];

        if (reports.length === 0) {
          return (
            <EmptyState
              title="No recordings yet"
              description="This shared recording link doesn't have any recordings yet. Recordings will appear here once they're uploaded."
            />
          );
        }

        // Sort reports by createdAt in descending order (newest first)
        const sortedReports = [...reports].sort((a: any, b: any) => {
          return (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
        });

        return sortedReports.map((report: any) => (
          <Report
            key={report._id}
            onDeleteSuccess={handleReportDelete}
            report={report}
          />
        ));
      }

      // Show folders
      if (filteredRecordingLinks.length === 0) {
        return recordingsSearchTerm ? (
          <NoResults desc="We haven't found such a recording link, but who knows..." />
        ) : (
          <EmptyState
            title="No shared recording links yet"
            description="Create a shared recording link to allow others to submit recordings to this workspace."
          />
        );
      }

      return filteredRecordingLinks.map((link: any) => {
        const folderName = link.name || link.title || "Recording Link";
        const createdBy = link.createdBy?.username || "Unknown";

        const createdAt = link.createdAt;
        const timeAgo = createdAt ? getTimeAgo(new Date(createdAt)) : "";
        const reports = link.reports || [];
        const previewReports = reports.slice(0, 3);
        const linkId = link._id;
        const linkToken = link.token;

        // Get or create ref for this menu
        if (!menuRefs.current[linkId]) {
          menuRefs.current[linkId] = React.createRef();
        }

        return (
          <div
            key={linkId}
            onClick={() => setSelectedFolder(linkId)}
            className="bg-white/[0.04] cursor-pointer rounded-[20px] h-[296px] p-3 hover:bg-gray-300 transition-all flex flex-col justify-between relative"
          >
            {/* Vertical menu button */}
            <div
              ref={menuRefs.current[linkId]}
              className="absolute top-5 right-5 z-20 flex items-center gap-2"
            >
              <AnimatePresence>
                {recordingLinkMenus[linkId] && (
                  <RecordingLinkOptions
                    recordingLinkToken={linkToken}
                    setShowConfirmDelete={(show) => {
                      if (show) {
                        setRecordingLinkToDelete({
                          token: linkToken,
                          name: folderName,
                          count: reports.length,
                        });
                      }
                    }}
                    setShowOptions={(show) => {
                      setRecordingLinkMenus((prev) => ({
                        ...prev,
                        [linkId]: show,
                      }));
                    }}
                  />
                )}
              </AnimatePresence>

              <button
                style={{ boxShadow: "0px 4px 24px 0px rgba(0, 0, 0, 0.35)" }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(
                    `${window.location.origin}/record/${linkToken}`,
                  );
                  showCustomToast("Link copied to clipboard", "check");
                }}
                className="w-[30px] z-[11] h-[30px] bg-white rounded-[50%] flex items-center justify-center"
              >
                <img src={Link} />
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setRecordingLinkMenus((prev) => ({
                    ...prev,
                    [linkId]: !prev[linkId],
                  }));
                }}
                style={{ boxShadow: "0px 4px 24px 0px rgba(0, 0, 0, 0.35)" }}
                className="w-[30px] h-[30px] bg-gray-400/[0.4] backdrop-blur-md rounded-[50%] flex items-center justify-center"
              >
                <img src={VerticalMenu} alt="Menu" />
              </button>
            </div>

            {/* Clickable folder content */}
            <div className="flex-1 cursor-pointer">
              {/* Preview Grid */}
              <div className="mb-2">
                <div className="flex gap-1.5 h-[200px]">
                  {/* Large video on the left */}
                  <div className="relative flex-1 bg-white/[0.04] rounded-l-[12px] rounded-r-[8px] overflow-hidden">
                    {previewReports[0]?.thumbnail ? (
                      <img
                        src={previewReports[0].thumbnail}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-400" />
                    )}
                  </div>

                  {/* Two stacked videos on the right */}
                  <div className="flex flex-col gap-1.5 flex-1">
                    <div className="relative flex-1 bg-white/[0.04] rounded-[8px] rounded-tr-[12px] overflow-hidden">
                      {previewReports[1]?.thumbnail ? (
                        <img
                          src={previewReports[1].thumbnail}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-400" />
                      )}
                    </div>
                    <div className="relative flex-1 bg-white/[0.04] rounded-[8px] rounded-br-[12px] overflow-hidden">
                      {previewReports[2]?.thumbnail ? (
                        <img
                          src={previewReports[2].thumbnail}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-400" />
                      )}
                      {reports.length > 3 && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <span className="text-white text-[16px] font-medium">
                            +{reports.length - 3}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer with folder name and creator */}
              <div>
                <div className="text-[14px] leading-[20px] p-1 font-medium text-white mb-2">
                  {folderName}
                </div>
                <div className="flex items-center justify-between p-1">
                  <div className="flex items-center gap-2">
                    <img
                      src={profilePictures[link.createdBy?.profilePicture - 1]}
                      className="border-[1px] w-[20px] h-[20px] border-white/[0.55] rounded-[50%]"
                      alt={createdBy}
                    />
                    <div className="text-[14px] font-medium leading-[20px] text-gray-900">
                      by {createdBy}
                    </div>
                  </div>
                  {timeAgo && (
                    <div className="text-[14px] font-medium leading-[20px] text-white/[0.48]">
                      {timeAgo}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      });
    }

    return null;
  };

  const getTimeAgo = (date: Date) => {
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return "just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    if (seconds < 2592000) return `${Math.floor(seconds / 604800)}w ago`;
    if (seconds < 31536000) return `${Math.floor(seconds / 2592000)}mo ago`;
    return `${Math.floor(seconds / 31536000)}y ago`;
  };

  return (
    <div
      className={`bg-gray-100 rounded-[24px] relative overflow-scroll no-scrollbar w-full ${
        currentWorkspace?.users.map((user) => user._id).includes(user?._id)
          ? "ml-[56px]"
          : ""
      } h-[calc(100vh-80px)]`}
    >
      <div className="flex justify-between h-[60px] items-center px-3 border-b-[1px] bg-gray-100 border-gray-300 z-[100] sticky top-0 left-0">
        <div className="flex gap-[2px] items-center">
          {selectedFolder && activeTab === "Shared Recordings" ? (
            <div className="flex items-center">
              <button
                onClick={() => setSelectedFolder(null)}
                className="px-3 rounded-[12px] py-2 text-[14px] h-[36px] transition-all hover:bg-gray-300 flex items-center gap-1.5 font-medium leading-[20px]"
              >
                <img src={ChevronLeftWhite} alt="Back" />
                Back
              </button>
              <div className="ml-[6px] mr-[16px] text-gray-700">/</div>
              <div className="text-[14px] font-medium leading-[20px] text-white">
                {recordingLinks.find((link) => link._id === selectedFolder)
                  ?.name ||
                  recordingLinks.find((link) => link._id === selectedFolder)
                    ?.title ||
                  "Recording Link"}
              </div>
            </div>
          ) : (
            tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  if (
                    tab === "Shared Recordings" &&
                    !currentWorkspace?.users
                      .map((user) => user._id)
                      .includes(user?._id)
                  ) {
                    showCustomToast("Join workspace to view", "exclamation");
                    return;
                  }
                  setActiveTab(tab);
                }}
                className={`px-3 py-1.5 text-[14px] h-[36px] hover:bg-gray-400 font-medium leading-[20px] rounded-[12px] transition-all ${
                  activeTab === tab ? "bg-gray-300" : "bg-transparent"
                }`}
              >
                {tab}
              </button>
            ))
          )}
        </div>

        <div className="w-[320px] h-[36px] border-[1px] border-gray-300 rounded-[12px] px-3 flex items-center gap-1.5">
          <img src={SearchLg} alt="Search" />
          <input
            value={currentSearchTerm}
            onChange={(e) => {
              setCurrentSearchTerm(e.target.value);
            }}
            placeholder="Search reports"
            className="w-full bg-transparent text-[14px] leading-[20px] focus:outline-none focus:ring-0 text-white placeholder:text-gray-800"
          />
        </div>
      </div>
      <div className="w-full p-4">
        <div className="grid min-[700px]:grid-cols-2 min-[950px]:grid-cols-3 min-[1250px]:grid-cols-4 min-[1920px]:grid-cols-5 gap-2 w-full">
          {renderContent()}
        </div>
      </div>

      <AnimatePresence>
        {user &&
          !currentWorkspace?.users
            .map((user) => user._id)
            .includes(user?._id) &&
          currentWorkspace?.reports && <JoinWorkspaceFooter />}

        {!token && <JoinWorkspaceFooter />}
      </AnimatePresence>

      <AnimatePresence>
        {recordingLinkToDelete && (
          <ConfirmDeleteRecordingLink
            recordingLink={recordingLinkToDelete}
            setShowConfirmDelete={(show) => {
              if (!show) setRecordingLinkToDelete(null);
            }}
            onDeleteSuccess={() => {
              removeRecordingLink(recordingLinkToDelete.token);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Reports;
